'use client';

import React, { memo } from 'react';
import { AnimatePresence, motion, Variants, type MotionProps } from 'motion/react';

const cn = (...classes: (string | false | null | undefined)[]) => classes.filter(Boolean).join(' ');

type AnimationType = 'text' | 'word' | 'character' | 'line';
type AnimationVariant =
  | 'fadeIn'
  | 'blurIn'
  | 'slideUp'
  | 'slideDown'
  | 'slideLeft'
  | 'slideRight'
  | 'scaleUp';
type ElementTag = 'div' | 'p' | 'span' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

interface TextAnimateProps extends Omit<MotionProps, 'children'> {
  children: string;
  className?: string;
  segmentClassName?: string;
  as?: ElementTag;
  by?: AnimationType;
  animation?: AnimationVariant;
  duration?: number;
  delay?: number;
  startOnView?: boolean;
  once?: boolean;
  center?: boolean;
  onComplete?: () => void; // ← new
}

const motionMap = {
  div: motion.div,
  p: motion.p,
  span: motion.span,
  h1: motion.h1,
  h2: motion.h2,
  h3: motion.h3,
  h4: motion.h4,
  h5: motion.h5,
  h6: motion.h6,
};

const presets: Record<AnimationVariant, Variants> = {
  fadeIn: { hidden: { opacity: 0, y: 15 }, show: { opacity: 1, y: 0 } },
  blurIn: {
    hidden: { opacity: 0, filter: 'blur(10px)' },
    show: { opacity: 1, filter: 'blur(0px)' },
  },
  slideUp: { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } },
  slideDown: { hidden: { opacity: 0, y: -20 }, show: { opacity: 1, y: 0 } },
  slideLeft: { hidden: { opacity: 0, x: 20 }, show: { opacity: 1, x: 0 } },
  slideRight: { hidden: { opacity: 0, x: -20 }, show: { opacity: 1, x: 0 } },
  scaleUp: { hidden: { opacity: 0, scale: 0.6 }, show: { opacity: 1, scale: 1 } },
};

const TextAnimateBase = ({
  children,
  className,
  segmentClassName,
  as = 'p',
  by = 'word',
  animation = 'fadeIn',
  duration = 0.8,
  delay = 0,
  startOnView = true,
  once = true,
  center = false,
  onComplete, // ← destructure
  ...props
}: TextAnimateProps) => {
  const Component = motionMap[as];
  const itemVariants = presets[animation];

  let segments: string[] = [];
  switch (by) {
    case 'word':
      segments = children.split(/(\s+)/);
      break;
    case 'character':
      segments = children.split('');
      break;
    case 'line':
      segments = children.split('\n');
      break;
    default:
      segments = [children];
  }

  return (
    <AnimatePresence mode="popLayout">
      <Component
        className={cn('whitespace-pre-wrap', center && 'text-center', className)}
        initial="hidden"
        whileInView={startOnView ? 'show' : undefined}
        animate={!startOnView ? 'show' : undefined}
        viewport={{ once }}
        transition={{
          staggerChildren: duration / segments.length,
          delayChildren: delay,
        }}
        onAnimationComplete={(definition) => {
          // ← new
          if (definition === 'show') onComplete?.();
        }}
        {...props}
      >
        {segments.map((segment, index) => (
          <motion.span
            key={index}
            variants={itemVariants}
            transition={{ duration: 0.35 }}
            className={cn(
              by === 'line' ? 'block' : 'inline-block whitespace-pre',
              segmentClassName,
            )}
          >
            {segment}
          </motion.span>
        ))}
      </Component>
    </AnimatePresence>
  );
};

export const TextAnimate = memo(TextAnimateBase);
export default TextAnimate;
